import { AnimatePresence, motion } from "motion/react";
import { TRASH_DAYS, trashItemOperations, trashItems, type TrashItem } from "@otrack/domain";
import { Button, Icon, type IconName } from "@otrack/ui";
import { EmptyState, PageHeader, rise } from "../components";
import { useApp, useDataset, useFactory } from "../context";
import { href } from "../router";

const TYPE: Record<TrashItem["type"], { label: string; icon: IconName }> = {
  client: { label: "Client", icon: "user" },
  project: { label: "Project", icon: "folder" },
  task: { label: "Task", icon: "target" },
  timeEntry: { label: "Time entry", icon: "clock" },
  invoice: { label: "Invoice", icon: "receipt" },
  event: { label: "Event", icon: "calendar" }
};

export function TrashPage() {
  const { now, act } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const items = trashItems(dataset, now);
  const days = (ts: number) => Math.max(1, Math.ceil((ts - now) / 86_400_000));
  const date = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });

  return <div className="ot-page">
    <PageHeader back={{ label: "Settings", href: href({ page: "settings" }) }} title="Trash" subtitle={`Deleted items wait here for ${TRASH_DAYS} days, then go for good.`}/>
    {items.length === 0 ? <EmptyState icon="tray" title="Trash is empty">Things you delete land here first, so a slip is never permanent.</EmptyState> : <motion.div className="ot-trash" {...rise(1)}>
      <AnimatePresence initial={false}>
        {items.map((item) => {
          const left = days(item.expiresAt);
          return <motion.div
            layout="position"
            className="ot-trash__row"
            key={`${item.type}:${item.id}`}
            exit={{ opacity: 0, x: 24, height: 0, paddingTop: 0, paddingBottom: 0, transition: { duration: 0.24, ease: [0.4, 0, 1, 1] } }}
          >
            <span className="ot-trash__icon"><Icon name={TYPE[item.type].icon}/></span>
            <div className="ot-trash__main">
              <strong>{item.label}</strong>
              <span>{TYPE[item.type].label} · deleted {date.format(item.deletedAt)}</span>
            </div>
            <div className="ot-trash__left" title={`Removed in ${left} ${left === 1 ? "day" : "days"}`}>
              <span className="ot-mono">{left}d left</span>
              <span className={`ot-trash__meter ${left <= 3 ? "is-soon" : ""}`}><i style={{ transform: `scaleX(${left / TRASH_DAYS})` }}/></span>
            </div>
            <div className="ot-trash__buttons">
              <Button icon="undo" onClick={() => void act(trashItemOperations(dataset, factory, item, "restore", Date.now()), `Restored ${item.label}`)}>Restore</Button>
              <Button tone="ghost" className="ot-button--quiet-danger" onClick={() => void act(trashItemOperations(dataset, factory, item, "discard", Date.now()), `Deleted ${item.label} for good`, { undoable: false })}>Delete now</Button>
            </div>
          </motion.div>;
        })}
      </AnimatePresence>
    </motion.div>}
  </div>;
}
